import { CommonModule } from '@angular/common';
import { HttpResponse } from '@angular/common/http';
import { Component, ElementRef, inject, OnInit, viewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import {
    ApiRequestService,
    DateUtilPipe,
    DialogComponent,
    DialogConfiguration,
    DialogService,
    generateTwoSegmentSlug,
    MAT_ICON,
    MetaComponent,
    MetaService,
    ProductPreviewComponent,
    slugValidator,
    TinyMCEHtmlEditorComponent,
    TOAST_STATE,
    ToastmessageService,
    TranslationPipe,
    TranslationService
} from '@prekog/common-ui';
import { forkJoin, Observable, of, switchMap, take, tap } from 'rxjs';
import { PressArticle } from '../press-article/model/press-article.model';
import { PressArticleComponent } from '../press-article/press-article.component';
import { Material } from '../product-list/material.model';
import { UploadToAnExistingFolderResponse } from '../upload-to-an-existing-folder.response';
import { FileType } from './enum/file-type.enum';
import { ProductFormMode } from './enum/product-form.enum';
import { AddProductRequest } from './use-cases/add-product/add-product.request';
import { AddProductUseCase } from './use-cases/add-product/add-product.usecase';
import { GetProductRequest } from './use-cases/get-product/get-product.request';
import { GetProductResponse } from './use-cases/get-product/get-product.response';
import { GetProductUseCase } from './use-cases/get-product/get-product.usecase';
import { SetPreorderPublishedRequest } from './use-cases/set-preorder-published/set-preorder-published.request';
import { SetPreorderPublishedUseCase } from './use-cases/set-preorder-published/set-preorder-published.usecase';
import { UpdateProductRequest } from './use-cases/update-product/update-product.request';
import { UpdateProductUseCase } from './use-cases/update-product/update-product.usecase';
import { UploadFileResponse } from './use-cases/update-product/upload-file.response';

@Component({
    selector: 'pf-product-form',
    templateUrl: './product-form.component.html',
    styleUrls: ['./product-form.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        MatIconModule,
        MatCheckboxModule,
        TranslationPipe,
        DateUtilPipe,
        TinyMCEHtmlEditorComponent,
        MetaComponent,
        RouterModule,
        ProductPreviewComponent,
        PressArticleComponent
    ]
})
export class ProductFormComponent implements OnInit {
    protected readonly ProductFormMode = ProductFormMode;
    readonly fileCoverInput = viewChild<ElementRef>('fileCoverInput');
    readonly fileBookExtractInput = viewChild<ElementRef>('fileBookExtractInput');
    mode: ProductFormMode = ProductFormMode.Add;
    product: Material = {};
    productForm?: FormGroup;
    htmlForm?: FormGroup;
    pressArticleList: PressArticle[] = [];
    addCoverFile = false;
    addBookExtractFile = false;
    updateCoverFile = false;
    updateBookExtractFile = false;
    fileCover: File | null = null;
    fileBookExtract: File | null = null;
    coverPreviewUrl = '';
    url = '';
    bookExtractUrl = '';
    discountLimitedUntil = false;
    daysBetween = 0;
    originalRouterLink = '';
    slugManuallyEdited = false;
    productUrlPrefix = '/book';
    maxImageFileSize = 2000000;
    readonly maxImageFileSizeLabel = '2MB';
    maxPdfFileSize = 10000000;
    readonly maxPdfFileSizeLabel = '10MB';
    fileManagerUrl = inject('FILE_MANAGER_URL' as any) as string;
    uploadFileUrl = '/admin/uploadFile';
    uploadFileToAnExistingFolderUrl = '/admin/updatefile';
    private _metaService = inject(MetaService);
    private _translateService = inject(TranslationService);
    private _router = inject(Router);
    private _activatedRoute = inject(ActivatedRoute);
    private _fb = inject(FormBuilder);
    private _apiRequestService = inject(ApiRequestService);
    private _toastMessageService = inject(ToastmessageService);
    private _dialogService = inject(DialogService);
    private _addProductUsecase = inject(AddProductUseCase);
    private _updateProductUsecase = inject(UpdateProductUseCase);
    private _getProductUsecase = inject(GetProductUseCase);
    private _setPreorderPublishedUsecase = inject(SetPreorderPublishedUseCase);

    get isUpdateMode(): boolean {
        return this.mode === ProductFormMode.Update;
    }
    get isFormValid(): boolean {
        return !!this.productForm?.valid && !!this.htmlForm?.valid;
    }
    get previewMaterial(): Material {
        const formOriginalPrice = this.productForm?.get('originalPrice')?.value;
        const webshopPriceActive = !!this.productForm?.get('webshopPriceActive')?.value;
        const discountPriceActive = !!this.productForm?.get('discountPriceActive')?.value;
        const hasPreorder = !!this.productForm?.get('hasPreorder')?.value;
        const hasActivePrice = webshopPriceActive || discountPriceActive || hasPreorder;

        return {
            ...this.product,
            title: this.productForm?.get('title')?.value || this.product.title || '',
            author: this.productForm?.get('author')?.value || this.product.author || '',
            previewTitle: this.productForm?.get('previewTitle')?.value || undefined,
            previewAuthor: this.productForm?.get('previewAuthor')?.value || undefined,
            originalPrice: this._getDisplayPrice(formOriginalPrice),
            available: this._getAvailableAsLocalDate(),
            url: this.coverPreviewUrl || this.product.url,
            quantityOfSupply: this.productForm?.get('quantityofsupply')?.value ?? this.product.quantityOfSupply,
            webshopPriceActive: !!this.productForm?.get('webshopPriceActive')?.value,
            discountPriceActive: !!this.productForm?.get('discountPriceActive')?.value,
            hasPreorder: !!this.productForm?.get('hasPreorder')?.value,
            typeOfPrice: this._getTypeOfPrice(),
            discountPrice: this.productForm?.get('discountPrice')?.value ?? undefined,
            webshopPrice: this.productForm?.get('webshopPrice')?.value ?? undefined,
            preorderPrice: this.productForm?.get('preorderPrice')?.value ?? undefined,
            oldPrice: hasActivePrice ? formOriginalPrice : undefined,
            isAvailable: !!this.productForm?.get('isAvailable')?.value
        };
    }

    ngOnInit(): void {
        if (!sessionStorage.getItem('token')) {
            this._router.navigateByUrl('/');
            return;
        }
        this.createProductForm();
        this.createHtmlForm();
        const routerLink = this._activatedRoute.snapshot.paramMap.get('routerLink');
        if (routerLink) {
            this.mode = ProductFormMode.Update;
            this._loadProduct(routerLink);
        }
    }

    createProductForm(): void {
        this.productForm = this._fb.group({
            content: ['', [Validators.required]],
            author: ['', [Validators.required]],
            title: ['', [Validators.required]],
            previewTitle: [''],
            previewAuthor: [''],
            originalPrice: ['', [Validators.required, Validators.pattern('^-?[0-9]\\d*(\\.\\d{1,2})?$')]],
            isbn: ['', [Validators.required]],
            available: ['', [Validators.required]],
            bond: ['', [Validators.required]],
            dimension: ['', [Validators.required]],
            quantityofsupply: ['', [Validators.required]],
            routerLink: ['', [slugValidator()]],
            isAvailable: [false],
            isNovelty: [false],
            hasPreorder: [false],
            webshopPriceActive: [false],
            webshopPrice: [null],
            discountPriceActive: [false],
            discountPrice: [null],
            translator: [''],
            packagePrice: [null],
            illustrator: [''],
            subtitle: [''],
            preorderPrice: [null]
        });
    }

    createHtmlForm(): void {
        this.htmlForm = this._fb.group({
            html: ['', [Validators.required]]
        });
    }

    private _loadProduct(routerLink: string): void {
        const getProductRequest: GetProductRequest = { routerLink };
        (this._getProductUsecase.execute(getProductRequest) as Observable<GetProductResponse>)
            .subscribe((response) => {
                this.product = response.product!;
                this.pressArticleList = this.product.pressArticleList ?? [];
                this.originalRouterLink = this.product.routerLink ?? '';
                const formattedDate = this._formatAvailableForInput(this.product.available);
                this.productForm?.patchValue({
                    ...this.product,
                    available: formattedDate,
                    html: this.product.txt ?? '',
                    quantityofsupply: this.product.quantityOfSupply
                });

                if (this.product.discountLimitedUntil) {
                    this.calculateDaysBetweenIfDiscountLimitedUntil(this.product.discountLimitedUntil);
                }

                this.htmlForm?.controls['html'].setValue(this.product.txt ?? '');
            });
    }


    calculateDaysBetweenIfDiscountLimitedUntil(discountLimitedUntil: Date): void {
        const date1: Date = new Date();
        const date2: Date = new Date(discountLimitedUntil);
        const differenceInMs: number = Math.abs(date2.getTime() - date1.getTime());
        const millisecondsInDay: number = 1000 * 60 * 60 * 24;
        this.daysBetween = Math.floor(differenceInMs / millisecondsInDay);
    }

    onPressArticleListChange(pressArticleList: PressArticle[]): void {
        this.pressArticleList = pressArticleList;
    }

    shouldShowError(controlName: string): boolean {
        const control = this.productForm?.controls[controlName];
        return !!control?.invalid && (!!control?.dirty || !!control?.touched);
    }

    onTitleOrAuthorBlur(): void {
        const slugControl = this.productForm?.controls['routerLink'];
        if (!slugControl || this.slugManuallyEdited || slugControl.value?.trim()) {
            return;
        }
        const author = this.productForm?.controls['author'].value ?? '';
        const title = this.productForm?.controls['title'].value ?? '';
        if (author || title) {
            slugControl.setValue(generateTwoSegmentSlug(author, title));
        }
    }

    onSlugInput(): void {
        const slugControl = this.productForm?.controls['routerLink'];
        if (!slugControl) {
            return;
        }
        const value = (slugControl.value as string) ?? '';
        if (value.trim() === '') {
            this.slugManuallyEdited = false;
            const author = this.productForm?.controls['author'].value ?? '';
            const title = this.productForm?.controls['title'].value ?? '';
            if (author || title) {
                slugControl.setValue(generateTwoSegmentSlug(author, title));
            }
        } else {
            this.slugManuallyEdited = true;
        }
    }


    onChangeDiscountPriceActive(): void {
        if (this.productForm?.get('discountPriceActive')?.value) {
            this.productForm.patchValue({ webshopPriceActive: false, hasPreorder: false }, { emitEvent: false });
        }
    }

    onChangePreorderPriceActive(): void {
        if (this.productForm?.get('hasPreorder')?.value) {
            this.productForm.patchValue(
                { isAvailable: false, webshopPriceActive: false, discountPriceActive: false },
                { emitEvent: false }
            );
        }
    }

    onChangeWebshopPriceActive(): void {
        if (this.productForm?.get('webshopPriceActive')?.value) {
            this.productForm.patchValue({ discountPriceActive: false, hasPreorder: false }, { emitEvent: false });
        }
    }

    onChangeCover(event: Event): void {
        const target = event.target as HTMLInputElement;
        this.fileCover = target.files![0];
        if (this.fileCover.name.split('.').pop() === 'png') {
            this.addCoverFile = true;
            this.updateCoverFile = true;
            this.coverPreviewUrl = URL.createObjectURL(this.fileCover);
        } else {
            this.addCoverFile = false;
            this.updateCoverFile = false;
            this.coverPreviewUrl = '';
            this._toastMessageService.displayToastMessage(
                TOAST_STATE.warning,
                this._translateService.translate('productForm.toast.pngOnly'),
                MAT_ICON.warning
            );
            this._dismissError();
        }
    }

    onChangeBookExtract(event: Event): void {
        const target = event.target as HTMLInputElement;
        this.fileBookExtract = target.files![0];
        if (this.fileBookExtract.name.split('.').pop() === 'pdf') {
            this.addBookExtractFile = true;
            this.updateBookExtractFile = true;
        } else {
            this.addBookExtractFile = false;
            this.updateBookExtractFile = false;
            this._toastMessageService.displayToastMessage(
                TOAST_STATE.warning,
                this._translateService.translate('productForm.toast.pdfOnly'),
                MAT_ICON.warning
            );
            this._dismissError();
        }
    }

    clearSelectedCoverFile(): void {
        this.fileCoverInput()!.nativeElement.value = null;
        this.addCoverFile = false;
        this.updateCoverFile = false;
        this.coverPreviewUrl = '';
    }

    clearSelectedBookExtractFile(): void {
        this.fileBookExtractInput()!.nativeElement.value = null;
        this.addBookExtractFile = false;
        this.updateBookExtractFile = false;
    }

    setPreorderPublished() {
        const setPreorderPublishedRequest: SetPreorderPublishedRequest = {
            isbn: this.product.isbn
        };
        (this._setPreorderPublishedUsecase.execute(setPreorderPublishedRequest) as Observable<HttpResponse<unknown>>)
            .subscribe(() => {});
    }

    saveProduct(): void {
        if (this.isUpdateMode) {
            this.updateProduct();
        } else {
            this.addProduct();
        }
    }

    addProduct(): void {
        if (!this.addCoverFile) {
            this._toastMessageService.displayToastMessage(
                TOAST_STATE.warning,
                this._translateService.translate('productForm.toast.coverRequired'),
                MAT_ICON.warning
            );
            this._dismissError();
            return;
        }
        if ((this.fileCover?.size ?? 0) > this.maxImageFileSize) {
            this.displayFileSizeErrorToastMessage(this.maxImageFileSizeLabel);
            return;
        }
        if ((this.fileBookExtract?.size ?? 0) > this.maxPdfFileSize) {
            this.displayFileSizeErrorToastMessage(this.maxPdfFileSizeLabel);
            return;
        }

        const uploadObservables: Observable<unknown>[] = [];
        uploadObservables.push(
            this._apiRequestService.uploadFile(this.fileCover!, true, this.fileManagerUrl, this.uploadFileUrl).pipe(take(1))
        );
        if (this.addBookExtractFile && this.fileBookExtract) {
            uploadObservables.push(
                this._apiRequestService.uploadFile(this.fileBookExtract, true, this.fileManagerUrl, this.uploadFileUrl).pipe(take(1))
            );
        }

        forkJoin(uploadObservables)
            .pipe(
                switchMap((responses) => {
                    const uploadResponses = responses as UploadFileResponse[];
                    this.url = uploadResponses[0].downloadUri as string;
                    if (this.addBookExtractFile && uploadResponses.length > 1) {
                        this.bookExtractUrl = uploadResponses[1].downloadUri as string;
                    }

                    this.product = this._collectProductData();

                    return this._addProduct();
                }),
                switchMap((): Observable<unknown> => {
                    if (this._metaService.canSaveMeta(false)) {
                        this._metaService.saveMetaTag(
                            false,
                            this.product.routerLink!,
                            this.product.url!
                        );
                    }
                    return of(null);
                })

            )
            .subscribe(() => {
                this._router.navigateByUrl('/products');
            });
    }

    private _addProduct(): Observable<HttpResponse<unknown>> {
        const product: Material = this._collectProductData();
        this.product.routerLink = product.routerLink;
        const addProductRequest: AddProductRequest = {
            product: product,
            discountLimitedUntil: this.discountLimitedUntil
        };
        return this._addProductUsecase.execute(addProductRequest) as Observable<HttpResponse<unknown>>;
    }

    private _collectProductData(isUpdate = false): Material {
        const author = (this.productForm?.controls['author'].value as string) ?? '';
        const title = (this.productForm?.controls['title'].value as string) ?? '';
        let slug = ((this.productForm?.controls['routerLink'].value as string) ?? '').trim();
        if (!slug) {
            slug = generateTwoSegmentSlug(author, title);
        }

        const availableStr = this.productForm?.controls['available'].value;

        const product: Material = {
            ...(isUpdate && { id: this.product?.id }),
            content: this.productForm?.controls['content'].value as string | undefined,
            author: this.productForm?.controls['author'].value as string | undefined,
            title: this.productForm?.controls['title'].value as string | undefined,
            originalPrice: this.productForm?.controls['originalPrice'].value as number | undefined,
            isbn: this.productForm?.controls['isbn'].value as number | undefined,
            available: availableStr || (isUpdate ? this.product?.available : undefined),
            bond: this.productForm?.controls['bond'].value as string | undefined,
            dimension: this.productForm?.controls['dimension'].value as string | undefined,
            quantityOfSupply: this.productForm?.controls['quantityofsupply'].value as number | undefined,
            routerLink: slug,
            isAvailable: !!this.productForm?.controls['isAvailable'].value,
            isNovelty: !!this.productForm?.controls['isNovelty'].value,
            webshopPriceActive: !!this.productForm?.controls['webshopPriceActive'].value,
            discountPriceActive: !!this.productForm?.controls['discountPriceActive'].value,
            discountPrice: this.productForm?.controls['discountPrice'].value as number,
            hasPreorder: !!this.productForm?.controls['hasPreorder'].value,
            translator: this.productForm?.controls['translator'].value as string | undefined,
            packagePrice: this.productForm?.controls['packagePrice'].value as number,
            webshopPrice: this.productForm?.controls['webshopPrice'].value as number,
            illustrator: this.productForm?.controls['illustrator'].value as string | undefined,
            subtitle: this.productForm?.controls['subtitle'].value as string | undefined,
            preorderPrice: this.productForm?.controls['preorderPrice'].value as number,
            previewTitle: this.productForm?.controls['previewTitle'].value as string | undefined,
            previewAuthor: this.productForm?.controls['previewAuthor'].value as string | undefined,
            url: isUpdate ? (this.url || this.product?.url) : this.url,
            txt: this.htmlForm?.controls['html'].value as string | undefined,
            pressArticleList: this.pressArticleList,
            bookExtractUrl: isUpdate
                ? (this.bookExtractUrl || this.product?.bookExtractUrl)
                : (this.bookExtractUrl || undefined)
        };

        if (isUpdate && this.product) {
            product.serieId = this.product.serieId;
            product.serieNumber = this.product.serieNumber;
            product.orderNumber = this.product.orderNumber;
        }

        return product;
    }

    updateProduct(): void {
        if (!this.productForm?.valid) {
            this._toastMessageService.displayToastMessage(
                TOAST_STATE.error,
                this._translateService.translate('productForm.toast.requiredFields'),
                MAT_ICON.error
            );
            this._dismissError();
            return;
        }
        if ((this.fileCover?.size ?? 0) > this.maxImageFileSize) {
            this.displayFileSizeErrorToastMessage(this.maxImageFileSizeLabel);
            return;
        }
        if ((this.fileBookExtract?.size ?? 0) > this.maxPdfFileSize) {
            this.displayFileSizeErrorToastMessage(this.maxPdfFileSizeLabel);
            return;
        }

        let productUpdateObservable: Observable<unknown> = of(null);
        if (this.updateCoverFile && this.product.url) {
            productUpdateObservable = productUpdateObservable.pipe(
                switchMap(() => this._updateFile(this.fileCover!, this.product.url!, FileType.FILECOVER))
            );
        }
        if (this.updateBookExtractFile) {
            productUpdateObservable = productUpdateObservable.pipe(
                switchMap(() =>
                    this.product.bookExtractUrl
                        ? this._updateFile(this.fileBookExtract!, this.product.bookExtractUrl!, FileType.BOOKEXTRACT)
                        : this._uploadFile(this.fileBookExtract!, FileType.BOOKEXTRACT)
                )
            );
        }

        productUpdateObservable = productUpdateObservable.pipe(switchMap(() => this._updateProduct()));
        const routerLinkControl = this.productForm?.controls['routerLink'];
        const currentRouterLink = ((routerLinkControl?.value as string) ?? '').trim();
        const newRouterLink = currentRouterLink !== this.originalRouterLink ? currentRouterLink : null;

        productUpdateObservable.subscribe(() => {
            const canSaveMeta = this._metaService.canSaveMeta(true);
            if (canSaveMeta || newRouterLink) {
                this._metaService.saveMetaTag(true, newRouterLink ?? this.originalRouterLink, this.product.url!, this.originalRouterLink);
            }
            if (newRouterLink) {
                this.originalRouterLink = newRouterLink;
                this._router.navigateByUrl('/products/' + newRouterLink);
            }
        });
    }

    private _updateProduct(): Observable<HttpResponse<unknown>> {
        const product: Material = this._collectProductData(true);
        const updateProductRequest: UpdateProductRequest = {
            product: product,
            discountLimitedUntil: this.discountLimitedUntil
        };
        return this._updateProductUsecase.execute(updateProductRequest) as Observable<HttpResponse<unknown>>;
    }

    displayFileSizeErrorToastMessage(maxFileSizeLabel: string): void {
        this._toastMessageService.displayToastMessage(
            TOAST_STATE.error,
            this._translateService.translate('productForm.toast.fileSizeLimit').replace('{{maxSize}}', maxFileSizeLabel),
            MAT_ICON.error
        );
        this._dismissError();
    }

    private _updateFile(file: File, downloadUrl: string, fileType: FileType): Observable<unknown> {
        if (!file) {
            return of(null);
        }
        const folderName = downloadUrl.split('/').pop() as string;
        return this._apiRequestService.updateFile(file, folderName, true, this.fileManagerUrl, this.uploadFileToAnExistingFolderUrl).pipe(
            tap((response) => {
                const responseMessageObject = response as UploadToAnExistingFolderResponse;
                if (responseMessageObject.downloadUri) {
                    switch (fileType) {
                        case FileType.BOOKEXTRACT:
                            this.bookExtractUrl = responseMessageObject.downloadUri;
                            this.updateBookExtractFile = false;
                            break;
                        case FileType.FILECOVER:
                            this.url = responseMessageObject.downloadUri;
                            this.updateCoverFile = false;
                            break;

                    }
                }
            })
        );
    }

    private _uploadFile(file: File, fileType: FileType): Observable<unknown> {
        if (!file) {
            return of(null);
        }
        return this._apiRequestService.uploadFile(file, true, this.fileManagerUrl, this.uploadFileUrl).pipe(
            tap((response) => {
                const uploadFileResponse = response as UploadFileResponse;
                const downloadUri = uploadFileResponse?.downloadUri;

                if (downloadUri) {
                    switch (fileType) {
                        case FileType.BOOKEXTRACT:
                            this.product.bookExtractUrl = downloadUri;
                            this.updateBookExtractFile = false;
                            break;
                        case FileType.FILECOVER:
                            this.product.url = downloadUri;
                            this.updateCoverFile = false;
                            break;
                    }
                }
            })
        );
    }

    confirmSetPreorderPublished(): void {
        const dialogconfiguration: DialogConfiguration = {
            messageKey: 'products.preorderPublishConfirmMessage',
            targetData: this.product.author + ':' + this.product.title
        }
        this._dialogService.openDialog(
            DialogComponent,
            dialogconfiguration,
            () => {
                this.setPreorderPublished()
            }
        );
    }

    private _getDisplayPrice(formOriginalPrice: number | undefined): number | undefined {
        if (this.productForm?.get('hasPreorder')?.value) {
            return this.productForm?.get('preorderPrice')?.value ?? this.product.preorderPrice;
        }
        if (this.productForm?.get('webshopPriceActive')?.value) {
            return this.productForm?.get('webshopPrice')?.value ?? undefined;
        }
        if (this.productForm?.get('discountPriceActive')?.value) {
            return this.productForm?.get('discountPrice')?.value ?? undefined;
        }
        return formOriginalPrice || this.product.originalPrice;
    }

    private _formatAvailableForInput(available: Date | string | undefined): string {
        if (!available) {
            return '';
        }
        if (typeof available === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(available)) {
            return available.substring(0, 10);
        }
        const date = new Date(available);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    private _getAvailableAsLocalDate(): Date | undefined {
        const value = this.productForm?.get('available')?.value as string;
        if (!value) {
            return this.product.available;
        }
        const [year, month, day] = value.split('-').map(Number);
        return new Date(year, month - 1, day);
    }

    private _getTypeOfPrice(): string {
        if (this.productForm?.get('hasPreorder')?.value) {
            return this._translateService.translate('price.typeOfPrice.preorder');
        }
        if (this.productForm?.get('webshopPriceActive')?.value) {
            return this._translateService.translate('price.typeOfPrice.webshop');
        }
        if (this.productForm?.get('discountPriceActive')?.value) {
            return this._translateService.translate('price.typeOfPrice.discount');
        }
        return this._translateService.translate('price.typeOfPrice.default');
    }

    private _dismissError(): void {
        setTimeout(() => {
            this._toastMessageService.dismissToast();
        }, 3000);
    }
}
